const DB_NAME = 'ExamOfflineDB';
const STORE_NAME = 'pendingAnswers';
const DB_VERSION = 1;

class NetworkSyncService {
  constructor() {
    this.db = null;
    this.initDB();
  }

  initDB() {
    return new Promise((resolve, reject) => {
      const request = window.indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = (event) => {
        console.error('IndexedDB error:', event.target.error);
        reject(event.target.error);
      };

      request.onsuccess = (event) => {
        this.db = event.target.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          // Use a combination of examId and questionId as a unique key if needed, or an auto-increment key
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
          store.createIndex('examId', 'examId', { unique: false });
        }
        if (!db.objectStoreNames.contains('examState')) {
          db.createObjectStore('examState', { keyPath: 'examId' });
        }
      };
    });
  }

  async getDB() {
    if (this.db) return this.db;
    return await this.initDB();
  }

  async queueAnswer(answerData) {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      
      // Ensure we add a timestamp
      const dataWithTimestamp = {
        ...answerData,
        queuedAt: new Date().getTime(),
      };

      const request = store.add(dataWithTimestamp);

      request.onsuccess = () => resolve(request.result);
      request.onerror = (event) => reject(event.target.error);
    });
  }

  async getPendingAnswers() {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result);
      request.onerror = (event) => reject(event.target.error);
    });
  }

  async removePendingAnswer(id) {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(id);

      request.onsuccess = () => resolve();
      request.onerror = (event) => reject(event.target.error);
    });
  }

  async clearPendingAnswers() {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.clear();

      request.onsuccess = () => resolve();
      request.onerror = (event) => reject(event.target.error);
    });
  }

  async saveExamState(examId, stateData) {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['examState'], 'readwrite');
      const store = transaction.objectStore('examState');
      const request = store.put({ examId, ...stateData });

      request.onsuccess = () => resolve();
      request.onerror = (event) => reject(event.target.error);
    });
  }

  async clearExamState(examId) {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['examState'], 'readwrite');
      const store = transaction.objectStore('examState');
      const request = store.delete(examId);

      request.onsuccess = () => resolve();
      request.onerror = (event) => reject(event.target.error);
    });
  }

  async getExamState(examId) {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['examState'], 'readonly');
      const store = transaction.objectStore('examState');
      const request = store.get(examId);

      request.onsuccess = () => resolve(request.result);
      request.onerror = (event) => reject(event.target.error);
    });
  }

  async clearAll() {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME, 'examState'], 'readwrite');
      transaction.objectStore(STORE_NAME).clear();
      transaction.objectStore('examState').clear();

      transaction.oncomplete = () => resolve();
      transaction.onerror = (event) => reject(event.target.error);
    });
  }
}

const networkSyncService = new NetworkSyncService();
export default networkSyncService;
